import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useRef } from 'react';

import { SocketContext } from '../providers/SocketProvider/SocketProvider';
import { TimesyncContext } from '../providers/TimesyncProvider/TimesyncProvider';
import { Game } from '../services/backendService/types';
import { useDebouncedCallback } from './useDebouncedCallback';
import { gameCacheKey } from './useGame';
import { newRelicNoticeError } from './useNewRelic';
import useRouterMatch from './useRouterMatch';

export type GameSavePayload = {
  game: Game;
  onSuccessFunction?: (gameId: string) => void;
};

function useGameSaveSocket() {
  const { tenantId } = useRouterMatch();
  const client = useQueryClient();
  const { gameAPI } = useContext(SocketContext);
  const latestSave = useRef<number | null>(null);
  const { now } = useContext(TimesyncContext);

  const { mutate: autoSave, isLoading, isError, isSuccess, isIdle } = useMutation({
    mutationFn: (payload: GameSavePayload) => {
      if (!gameAPI) return Promise.resolve(undefined);
      const modifiedAt = payload.game.modifiedAt;
      if (!modifiedAt) return Promise.reject();
      if (latestSave.current && modifiedAt !== latestSave.current) {
        return Promise.reject({ status: -1 });
      }

      return gameAPI.game.save({
        ...payload.game,
        tenantId,
        modifiedAt,
      });
    },
    onSuccess: (_, { game, onSuccessFunction }) => {
      if (latestSave.current && latestSave.current !== game.modifiedAt) return;
      const cacheKeySuccess = [gameCacheKey, game.id, 'success'];
      client.setQueryData<Game | undefined>(cacheKeySuccess, game);
      if (onSuccessFunction) {
        onSuccessFunction(game.id);
      }
    },
    onMutate: async mutatePayload => {
      const mutateBody = mutatePayload.game;
      const cacheKey = [gameCacheKey, mutateBody?.id];
      const rollbackCacheKey = [gameCacheKey, mutateBody?.id, 'success'];
      await client.cancelQueries(cacheKey);
      const previousGameSuccess = client.getQueryData<Game>(rollbackCacheKey);
      client.setQueryData<Game | undefined>(cacheKey, game => {
        if (!game) return game;
        return mutatePayload.game;
      });
      // Return the snapshotted value
      return () => client.setQueryData(cacheKey, previousGameSuccess);
    },
    retry: (failureCount, error: { status: number }) => {
      if (error.status === 409 || error.status === -1) {
        // We throw 409 in the backend if the paylpad is not saved and published. Due to racecondition and conflict we don't want to retry.
        // usecase: the user is saving a lot of times, and we only want to handle the last active save
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: () => 400,
    onError: (err, bodyWithError, rollback) => {
      newRelicNoticeError(err, 'Error while auto saving game, onError');

      // we're not rolling back if this is not the latest save
      if (bodyWithError.game.modifiedAt !== latestSave.current) {
        return;
      }

      //@ts-ignore
      return rollback();
    },
  });
  const autoSaveTimestampMutate = useCallback(
    (payload: GameSavePayload) => {
      if (!gameAPI) return Promise.resolve(undefined);
      const modifiedAt = now();

      latestSave.current = modifiedAt;
      return autoSave({ ...payload, game: { ...payload.game, modifiedAt } });
    },
    [autoSave, gameAPI, now],
  );

  const debouncedMutate = useDebouncedCallback(
    (payload: unknown) => autoSaveTimestampMutate(payload as GameSavePayload),
    { delay: 200, leading: false, trailing: true },
  );

  const autoSaveDebounced = useCallback(
    (payload: GameSavePayload) => {
      client.setQueryData<Game | undefined>([gameCacheKey, payload.game.id], existing =>
        existing ? payload.game : existing,
      );
      debouncedMutate(payload);
    },
    [client, debouncedMutate],
  );

  return {
    autoSave: autoSaveTimestampMutate,
    autoSaveDebounced,
    isLoading,
    isError,
    isSuccess,
    isIdle,
  };
}

export default useGameSaveSocket;
